/* eslint-disable @typescript-eslint/no-unsafe-return */
import * as glassEasel from 'glass-easel';
import { guid } from './utils';
import { TraitBehavior } from './behavior';
import { BehaviorBuilder, ComponentBuilder } from './builder';
/**
 * A component space with mini-program code manager
 */
export class CodeSpace {
    /** @internal */
    constructor(env, isMainSpace, publicComponents, globalCodeSpace) {
        /** @internal */
        this._$overallBehavior = null;
        this._$env = env;
        this._$isMainSpace = isMainSpace;
        this.space = new glassEasel.ComponentSpace('', globalCodeSpace === null || globalCodeSpace === void 0 ? void 0 : globalCodeSpace.space, globalCodeSpace === null || globalCodeSpace === void 0 ? void 0 : globalCodeSpace.space.styleScopeManager);
        this.space.__wxCodeSpace = this;
        this.styleScopeManager = this.space.styleScopeManager;
        this.staticConfigMap = Object.create(null);
        this.styleSheetMap = Object.create(null);
        this.compiledTemplateMap = Object.create(null);
        this.waitingAliasMap = Object.create(null);
        Object.keys(publicComponents).forEach((alias) => {
            const is = publicComponents[alias];
            if (this.waitingAliasMap[is])
                this.waitingAliasMap[is].push(alias);
            else
                this.waitingAliasMap[is] = [alias];
        });
        this._$sharedStyleScope = this.styleScopeManager.register('');
        this.space.setSharedStyleScope(this._$sharedStyleScope);
        this._$styleIsolationMap = Object.create(null);
    }
    isMainSpace() {
        return this._$isMainSpace;
    }
    /**
     * Get the underlying component space
     */
    getComponentSpace() {
        return this.space;
    }
    /**
     * Add a behavior for all components
     *
     * Must be called before any component registration so that the component will use this behavior.
     * Set to `null` to disable this behavior.
     */
    setOverallBehavior(behavior) {
        this._$overallBehavior = behavior;
    }
    /**
     * Import another component space as a plugin (must be in the same environment)
     *
     * Components from the imported `codeSpace` can be used with URLs.
     * If `domainAlias` is provided, the URL format is `plugin://DOMAIN_ALIAS/COMPONENT_SPACE_EXPORT_ALIAS` .
     * If `privateImport` is enabled, another URL format is `plugin-private://DOMAIN/COMPONENT_IS` .
     */
    importCodeSpace(id, domainAlias, privateImport = false) {
        const cs = this._$env.getCodeSpace(id);
        if (!cs) {
            throw new Error(`There is no space "${id}" in the environment`);
        }
        if (domainAlias)
            this.space.importSpace(`plugin://${domainAlias}`, cs.space, false);
        if (privateImport)
            this.space.importSpace(`plugin-private://${id}`, cs.space, true);
    }
    /**
     * Add a style sheet URL with style scope name
     *
     * The URL should be recognized by the target backend.
     * The `path` should not contain the `.wxss` suffix.
     */
    addStyleSheet(path, styleSheetUrl, styleScopeName) {
        this.styleSheetMap[path] = { url: styleSheetUrl, styleScopeName };
    }
    /**
     * Get a style sheet URL
     *
     * The URL should be recognized by the target backend.
     * The `path` should not contain the `.wxss` suffix.
     */
    getStyleSheet(path) {
        var _a;
        return (_a = this.styleSheetMap[path]) === null || _a === void 0 ? void 0 : _a.url;
    }
    getStyleScopeName(path) {
        var _a;
        return (_a = this.styleSheetMap[path]) === null || _a === void 0 ? void 0 : _a.styleScopeName;
    }
    /**
     * Add a compiled template
     *
     * The content is the execution result of the generated string of the template compiler.
     * The `path` should not contain the `.wxml` suffix.
     */
    addCompiledTemplate(path, content) {
        this.compiledTemplateMap[path] = content;
    }
    /**
     * Get a compiled template
     *
     * The content is the execution result of the generated string of the template compiler.
     * The `path` should not contain the `.wxml` suffix.
     */
    getCompiledTemplate(path) {
        return this.compiledTemplateMap[path];
    }
    /**
     * Add a static JSON config
     *
     * The `path` should not contain the `.json` suffix.
     */
    addComponentStaticConfig(path, content) {
        this.staticConfigMap[path] = content;
    }
    /**
     * Get a static JSON config
     *
     * The `path` should not contain the `.json` suffix.
     */
    getComponentStaticConfig(path) {
        return this.staticConfigMap[path];
    }
    /** @internal */
    prepareComponentOptions(is, options) {
        // accept some fields from static JSON config
        const staticConfig = this.staticConfigMap[is];
        const pureDataPatternString = staticConfig === null || staticConfig === void 0 ? void 0 : staticConfig.pureDataPattern;
        const pureDataPattern = pureDataPatternString
            ? new RegExp(pureDataPatternString)
            : options === null || options === void 0 ? void 0 : options.pureDataPattern;
        // calculate style scope
        const addGlobalClass = staticConfig === null || staticConfig === void 0 ? void 0 : staticConfig.addGlobalClass;
        let styleIsolation = staticConfig === null || staticConfig === void 0 ? void 0 : staticConfig.styleIsolation;
        if (styleIsolation === undefined) {
            if (staticConfig === null || staticConfig === void 0 ? void 0 : staticConfig.component) {
                styleIsolation = addGlobalClass ? "apply-shared" /* StyleIsolation.ApplyShared */ : "isolated" /* StyleIsolation.Isolated */;
            }
            else {
                styleIsolation = "shared" /* StyleIsolation.Shared */;
            }
        }
        let styleScope;
        let extraStyleScope;
        if (styleIsolation === "apply-shared" /* StyleIsolation.ApplyShared */ ||
            styleIsolation === "page-apply-shared" /* StyleIsolation.PageApplyShared */) {
            styleScope = this.styleScopeManager.register(this.getStyleScopeName(is) || `__${guid()}`);
            extraStyleScope = this._$sharedStyleScope;
        }
        else if ((styleIsolation === "shared" /* StyleIsolation.Shared */ || styleIsolation === "page-shared" /* StyleIsolation.PageShared */) &&
            this._$isMainSpace) {
            styleScope = this._$sharedStyleScope;
        }
        else {
            styleScope = this.styleScopeManager.register(this.getStyleScopeName(is) || `__${guid()}`);
        }
        // construct component options
        const ret = {
            idPrefixGenerator: guid,
            multipleSlots: options === null || options === void 0 ? void 0 : options.multipleSlots,
            dynamicSlots: options === null || options === void 0 ? void 0 : options.dynamicSlots,
            pureDataPattern,
            virtualHost: options === null || options === void 0 ? void 0 : options.virtualHost,
            styleScope,
            extraStyleScope,
            dataDeepCopy: options === null || options === void 0 ? void 0 : options.dataDeepCopy,
            propertyPassingDeepCopy: options === null || options === void 0 ? void 0 : options.propertyPassingDeepCopy,
            propertyEarlyInit: options === null || options === void 0 ? void 0 : options.propertyEarlyInit,
            propertyComparer: options === null || options === void 0 ? void 0 : options.propertyComparer,
        };
        return [ret, styleIsolation];
    }
    /**
     * Create a component definition environment
     *
     * During the sync execution of `cb` , `Component` and `Behavior` global vars will be available.
     * `globalObject` should be the JavaScript global object, a.k.a. `window` in DOM.
     * Some global variables, a.k.a `Component` `Behavior` `Page` ,
     * will be written into `globalObject` .
     * `path` should be the component path (without ".js" extension).
     */
    globalComponentEnv(globalObject, path, cb) {
        return this.componentEnv(path, ({ Page, Component, Behavior }) => {
            /* eslint-disable @typescript-eslint/no-unsafe-member-access */
            /* eslint-disable @typescript-eslint/no-unsafe-assignment */
            const oldPage = globalObject.Page;
            const oldComponent = globalObject.Component;
            const oldBehavior = globalObject.Behavior;
            globalObject.Page = Page;
            globalObject.Component = Component;
            globalObject.Behavior = Behavior;
            const ret = cb();
            globalObject.Page = oldPage;
            globalObject.Component = oldComponent;
            globalObject.Behavior = oldBehavior;
            /* eslint-enable @typescript-eslint/no-unsafe-member-access */
            /* eslint-enable @typescript-eslint/no-unsafe-assignment */
            return ret;
        });
    }
    /**
     * Create a component definition environment
     *
     * Like `globalComponentEnv` , but the global variables are given as callback arguments.
     */
    componentEnv(path, cb) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        // The page constructor
        function pageConstructor(definition) {
            return self.component(path).pageDefinition(definition).register();
        }
        function componentConstructor(definition) {
            if (definition !== undefined) {
                return self.component(path).definition(definition).register();
            }
            return self.component(path);
        }
        function behaviorConstructor(definition) {
            if (definition !== undefined) {
                return self.behavior().definition(definition).register();
            }
            return self.behavior();
        }
        behaviorConstructor.trait = self.traitBehavior.bind(self);
        // prepare global vars and execute
        return cb({
            Page: pageConstructor,
            Component: componentConstructor,
            Behavior: behaviorConstructor,
        });
    }
    /**
     * Build a component outside of a definition environment
     *
     * The method do not need a definition environment, so the global object pollution is avoided.
     * `path` should be the component path (without ".js" extension).
     */
    component(path) {
        return ComponentBuilder.create(this, path, this.waitingAliasMap[path]);
    }
    /**
     * Build a behavior outside of a definition environment
     *
     * The method do not need a definition environment, so the global object pollution is avoided.
     */
    behavior() {
        return BehaviorBuilder.create(this);
    }
    traitBehavior(trans) {
        if (trans === undefined) {
            return new TraitBehavior(this.space.defineTraitBehavior());
        }
        return new TraitBehavior(this.space.defineTraitBehavior(trans));
    }
}
//# sourceMappingURL=space.js.map